import { useState } from 'react'

const Sidebar = ({ user, chatHistory, currentChatId, onNewChat, onSelectChat, onLogout, isLoggingOut, isLoadingChats, isOpen, onToggle }) => {
  const [showUserMenu, setShowUserMenu] = useState(false)

  // Formatear fecha para mostrar
  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now - date)
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return 'Hoy'
    if (diffDays === 1) return 'Ayer'
    if (diffDays < 7) return `Hace ${diffDays} días`
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })
  }

  // Truncar título si es muy largo
  const truncateTitle = (title, maxLength = 28) => {
    if (title.length <= maxLength) return title
    return title.substring(0, maxLength) + '...'
  }

  return (
    <>
      {/* Botón hamburguesa para móvil */}
      <button
        onClick={onToggle}
        className="fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md md:hidden"
        aria-label="Toggle sidebar"
      >
        <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {isOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Overlay para móvil */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/30 z-30 md:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-full w-72 bg-gray-50 border-r border-gray-200 z-40 transform transition-transform duration-300 ease-in-out flex flex-col ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0`}
      >
        {/* Header con info del usuario */}
        <div className="p-4 border-b border-gray-200">
          <div 
            className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 p-2 rounded-lg transition-colors relative"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            {/* Avatar */}
            <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0">
              {user?.nombre ? user.nombre.charAt(0).toUpperCase() : 'U'}
            </div>
            {/* Nombre y teléfono */}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-800 truncate">
                {user?.nombre || 'Usuario'}
              </p>
              <p className="text-sm text-gray-500 truncate">
                {user?.telefono || user?.email || ''}
              </p>
            </div>
            {/* Flecha */}
            <svg className={`w-4 h-4 text-gray-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {/* Menú desplegable del usuario */}
          {showUserMenu && (
            <div className="mt-2 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => {
                  setShowUserMenu(false)
                  onLogout()
                }}
                disabled={isLoggingOut}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoggingOut ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                )}
                {isLoggingOut ? 'Cerrando sesión...' : 'Cerrar sesión'}
              </button>
            </div>
          )}
        </div>

        {/* Botón Nueva Charla */}
        <div className="p-4">
          <button
            onClick={onNewChat}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nueva charla
          </button>
        </div>

        {/* Lista de charlas */}
        <div className="flex-1 overflow-y-auto px-2">
          <p className="px-2 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider">
            Charlas recientes
          </p>
          
          {isLoadingChats ? (
            <div className="flex justify-center py-6">
              <svg className="w-5 h-5 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          ) : chatHistory.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-500 text-center">
              No hay charlas anteriores
            </p>
          ) : (
            <div className="space-y-1">
              {chatHistory.map((chat) => (
                <button
                  key={chat.id}
                  onClick={() => onSelectChat(chat.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors group ${
                    currentChatId === chat.id 
                      ? 'bg-gray-200 text-gray-900' 
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <span className="flex-1 truncate text-sm">
                      {truncateTitle(chat.title)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5 ml-6">
                    {formatDate(chat.created_at)}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer con logo */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center justify-center gap-2 opacity-50">
            <img 
              src="./logotype.png" 
              alt="Doctor Vet" 
              className="h-6 w-auto"
            />
          </div>
        </div>
      </aside>
    </>
  )
}

export default Sidebar
